export const DUPLICATE_WINDOW_DAYS = 3;

const nameKey = (transaction) => (transaction.where || '').trim().toLowerCase();
const day = (transaction) => transaction.transaction_date.clone().startOf('day');

/** "12,34": a pair as the server keeps it, lower id first. */
export const pairKey = (a, b) => (a < b ? `${a},${b}` : `${b},${a}`);

/**
 * Groups transactions that may be the same one entered twice: the same amount, within
 * DUPLICATE_WINDOW_DAYS of the earliest in the group, which catches a hand entry and its import
 * posting a day or two apart. A group where two share a name and a day is `likely`; the rest are
 * possible. Likely groups come first, then the most recent.
 *
 * A group is `dismissed` when every pair in it was marked as not duplicates, so a new transaction
 * joining a dismissed group brings the group back.
 *
 * Unsaved transactions, deleted ones and those without an amount or a date are left out.
 *
 * @param {Iterable<Transaction>} transactions
 * @param {Array<[number, number]>} notDuplicatePairs
 * @returns {{groups: Array<DuplicateGroup>, dismissed: Array<DuplicateGroup>}}
 *          DuplicateGroup is {key: string, transactions: Transaction[], likely: boolean}, its
 *          transactions oldest first, and `key` their ids in order.
 */
export default function findDuplicates(transactions, notDuplicatePairs = []) {
	const byAmount = new Map();
	for(const transaction of transactions) {
		if(transaction.deleted || !(transaction.id > 0) || !transaction.amount || !transaction.transaction_date) {
			continue;
		}
		if(!byAmount.has(transaction.amount)) {
			byAmount.set(transaction.amount, []);
		}
		byAmount.get(transaction.amount).push(transaction);
	}

	const dismissedPairs = new Set(notDuplicatePairs.map(([a, b]) => pairKey(Number(a), Number(b))));
	const groups = [];
	const dismissed = [];
	byAmount.forEach((sameAmount) => {
		sameAmount.sort((a, b) => day(a).diff(day(b)) || a.id - b.id);
		let start = 0;
		while(start < sameAmount.length) {
			const first = day(sameAmount[start]);
			let end = start + 1;
			while(end < sameAmount.length && day(sameAmount[end]).diff(first, 'days') <= DUPLICATE_WINDOW_DAYS) {
				end++;
			}
			if(end - start > 1) {
				const members = sameAmount.slice(start, end);
				const group = { key: members.map((each) => each.id).join(','), transactions: members, likely: isLikely(members) };
				(everyPair(members, (a, b) => dismissedPairs.has(pairKey(a.id, b.id))) ? dismissed : groups).push(group);
			}
			start = end;
		}
	});

	const latest = (group) => day(group.transactions[group.transactions.length - 1]).valueOf();
	const order = (a, b) => (b.likely - a.likely) || (latest(b) - latest(a));
	return { groups: groups.sort(order), dismissed: dismissed.sort(order) };
}

function somePair(members, test) {
	for(let i = 0; i < members.length; i++) {
		for(let j = i + 1; j < members.length; j++) {
			if(test(members[i], members[j])) {
				return true;
			}
		}
	}
	return false;
}

const everyPair = (members, test) => !somePair(members, (a, b) => !test(a, b));

const isLikely = (members) => somePair(members, (a, b) => nameKey(a) !== '' && nameKey(a) === nameKey(b) && day(a).isSame(day(b)));
